import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { restartableTask } from 'ember-concurrency';
import type { Query } from 'graphql-models';
import shipmentsQuery from 'walden-fast/graphql/shipments/shipments.graphql';
import type BoxApiService from 'walden-fast/services/box-api';
import type UserService from 'walden-fast/services/user';
import type {
  PaginationQueryParams,
  SortingQueryParams,
} from 'walden-fast/types/pagination';
import { SERVICE_NAMES } from 'walden-fast/utils/constants/services';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type AppServicesWaldenBoxListModel = Resolved<
  ReturnType<AppServicesWaldenBoxListRoute['model']>
>;

interface QueryParams extends PaginationQueryParams, SortingQueryParams {
  consignee?: string;
  search?: string;
  state?: string;
}

export default class AppServicesWaldenBoxListRoute extends Route {
  @service('box-api') declare boxApiService: BoxApiService;
  @service('user') declare userService: UserService;

  queryParams = {
    consignee: { refreshModel: true },
    page: { refreshModel: true },
    pageSize: { refreshModel: true },
    sortDirection: { refreshModel: true },
    sortField: { refreshModel: true },
    search: { refreshModel: true },
    state: { refreshModel: true },
  };

  model({
    consignee,
    page,
    pageSize,
    sortDirection,
    sortField,
    search,
    state,
  }: QueryParams) {
    const userCanAccessService = this.userService.canAccessService(
      SERVICE_NAMES.WALDEN_BOX,
    );
    if (userCanAccessService) {
      void this.fetchShipments.perform({
        consignee,
        page,
        pageSize,
        sortDirection,
        sortField,
        search,
        state,
      });
    }
    return this.fetchShipments;
  }

  fetchShipments = restartableTask(async (params: QueryParams) => {
    const data = await this.boxApiService.query<{
      shipmentsForShipper: Query['shipmentsForShipper'];
      consignees: Query['consignees'];
    }>({
      query: shipmentsQuery,
      variables: {
        filter: {
          search: params.search,
          shipmentState: params.state,
          consigneeId: params.consignee,
          shipmentType: 'BOX',
        },
        page: params.page || 1,
        first: params.pageSize || 20,
        ...(params.sortField &&
          params.sortDirection &&
          params.sortDirection !== 'none' && {
            orderBy: {
              column: params.sortField,
              order: params.sortDirection.toUpperCase(),
            },
          }),
      },
    });

    return data;
  });
}
